import React, { useState, useEffect } from 'react';
import { useTranslation } from '../utils/translations';
import { Trophy, ArrowRight, BookOpen } from 'lucide-react';
import { supabase, getLessonsTable } from '../utils/supabase';
import { type LanguageLevel } from '../types';

interface HighUnderstandingOptionsProps {
  currentLessonId: string;
  currentLevel: LanguageLevel;
  onPracticeAnyway: () => void;
  onGoToNextLesson: (nextLessonId: string) => void;
  nativeLanguage?: string;
}

export function HighUnderstandingOptions({
  currentLessonId,
  currentLevel,
  onPracticeAnyway,
  onGoToNextLesson,
  nativeLanguage
}: HighUnderstandingOptionsProps) {
  const { t } = useTranslation(nativeLanguage);
  const [nextLesson, setNextLesson] = useState<{ id: string; title: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const findNextLesson = async () => {
      try {
        const lessonsTable = getLessonsTable(nativeLanguage);
        console.log('Finding next lesson using table:', lessonsTable, 'for level:', currentLevel);

        // Get current lesson to find its order
        const { data: currentLesson } = await supabase
          .from(lessonsTable)
          .select('order_index, title')
          .eq('id', currentLessonId)
          .single();

        console.log('Current lesson data:', currentLesson);

        if (currentLesson) {
          // Find next lesson
          const { data: nextLessonData } = await supabase
            .from(lessonsTable)
            .select('id, title')
            .eq('level', currentLevel)
            .eq('is_review', false)
            .gt('order_index', currentLesson.order_index)
            .order('order_index', { ascending: true })
            .limit(1)
            .maybeSingle();

          console.log('Next lesson found:', nextLessonData);
          setNextLesson(nextLessonData);
        }
      } catch (error) {
        console.error('Error finding next lesson:', error);
      } finally {
        setLoading(false);
      }
    };

    findNextLesson();
  }, [currentLessonId, currentLevel, nativeLanguage]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400 to-orange-500 mb-4 animate-celebration-bounce">
            <Trophy className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            {t('Amazing!')}
          </h2>
          <p className="text-lg text-gray-700">
            {t('You understood 100% of the lesson!')}
          </p>
        </div>

        <div className="mb-6 p-4 bg-gradient-to-r from-blue-50 to-green-50 rounded-lg border-2 border-blue-200">
          <p className="text-gray-700 text-center">
            {t('Would you like to practice this lesson anyway, or move to the next one?')}
          </p>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : nextLesson ? (
            <>
              <button
                onClick={() => onGoToNextLesson(nextLesson.id)}
                className="w-full flex items-center justify-center px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 text-white rounded-full hover:from-green-600 hover:to-green-700 transition-all shadow-md hover:shadow-lg font-medium"
              >
                <ArrowRight className="h-5 w-5 mr-2" />
                {t('Go to Next Lesson')}
              </button>
              <div className="text-center py-1">
                <p className="text-sm text-gray-600">
                  {t('Next')}: <span className="font-medium">"{nextLesson.title}"</span>
                </p>
              </div>
            </>
          ) : (
            <div className="mb-4 p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-700 text-center">
                {t('This is the last lesson in your current level.')}
              </p>
            </div>
          )}

          <button
            onClick={onPracticeAnyway}
            className="w-full flex items-center justify-center px-6 py-3 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors font-medium"
          >
            <BookOpen className="h-5 w-5 mr-2" />
            {t('Practice Anyway')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes celebration-bounce {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
        .animate-celebration-bounce {
          animation: celebration-bounce 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
